import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import './index.css';
import './i18n';
import { HelmetProvider } from 'react-helmet-async';
import { MobileProvider } from './contexts/MobileContext';
import { TooltipProvider } from './components/ui/tooltip';
import { hydrateAuthToken } from './lib/auth-token';
import { isNotFound } from './lib/api-error';
import { reportClientError, reportUncaughtErrors } from './lib/error-reporting';
import { ErrorBoundary } from './components/ErrorBoundary';

reportUncaughtErrors();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Asking again will not make a missing thing appear.
      retry: (failureCount, error) => !isNotFound(error) && failureCount < 3,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Prepares native integrations before the first render:
 * - restores the persisted bearer token so the initial session request is
 *   authenticated (no-op on web);
 * - initializes native Google sign-in on Android (iOS needs its own OAuth
 *   client configured before the native flow can be enabled there).
 * Failures here must not block the app, so they are caught and logged.
 */
async function bootstrap() {
  try {
    await hydrateAuthToken();
    if (Capacitor.getPlatform() === 'android') {
      await SocialLogin.initialize({
        google: {
          webClientId: import.meta.env.GOOGLE_WEB_CLIENT_ID,
          mode: 'online',
        },
      });
    }
  } catch (error) {
    console.error('Native auth setup failed', error);
    reportClientError(error);
  }

  // Two shells over the same components: the web app, and the runners-only
  // Android app. APP_SHELL is replaced at build time, so the bundler keeps
  // only the branch taken and the other shell's pages are not shipped.
  const { default: App } =
    import.meta.env.APP_SHELL === 'runner' ? await import('./RunnerApp.tsx') : await import('./App.tsx');

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <MobileProvider>
              <ErrorBoundary>
                <App />
              </ErrorBoundary>
            </MobileProvider>
          </TooltipProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </HelmetProvider>
    </React.StrictMode>,
  );
}

void bootstrap();
